import { z } from 'zod';
import { DEFAULT_CURRENCY, PROJECT_MEMBER_LIMIT, SUPPORTED_CURRENCIES } from '../constants.js';
export const currencySchema = z.enum(SUPPORTED_CURRENCIES);
export const projectTypeSchema = z.enum(['RENOVATION', 'FINANCE']);
// Дата начала ремонта в формате YYYY-MM-DD
const startDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата должна быть в формате YYYY-MM-DD');
/**
 * Блоки главного экрана. Главным (крупным, первым) можно сделать любой из них:
 * кто-то ведёт бюджет, а кто-то пришёл только рисовать планировку.
 */
export const dashboardBlockSchema = z.enum(['FINANCE', 'PLANS', 'STAGES', 'MATERIALS', 'REPORTS']);
/** Порядок блоков по умолчанию — он же порядок в настройках. */
export const DASHBOARD_BLOCKS = ['FINANCE', 'PLANS', 'MATERIALS', 'STAGES', 'REPORTS'];
export const dashboardPrefsSchema = z.object({
    /** Что показывать первым и крупно. */
    main: dashboardBlockSchema,
    /** Что не показывать вовсе. Главный блок скрыть нельзя — нормализация его выкидывает. */
    hidden: z.array(dashboardBlockSchema).max(DASHBOARD_BLOCKS.length),
});
export const DEFAULT_DASHBOARD_PREFS = { main: 'FINANCE', hidden: [] };
/**
 * Настройки из БД могли устареть (блок переименовали) или прийти кривыми —
 * главный экран не должен из-за этого падать, поэтому непонятное заменяем
 * значением по умолчанию.
 */
export function parseDashboardPrefs(value) {
    const parsed = dashboardPrefsSchema.safeParse(value);
    if (!parsed.success)
        return DEFAULT_DASHBOARD_PREFS;
    const { main, hidden } = parsed.data;
    return { main, hidden: [...new Set(hidden)].filter((block) => block !== main) };
}
export const createProjectSchema = z.object({
    title: z.string().min(1).max(255),
    type: projectTypeSchema.default('RENOVATION'),
    currency: currencySchema.default(DEFAULT_CURRENCY),
    budgetTotal: z.number().positive().optional(),
    area: z.number().positive().optional(),
    startDate: startDateSchema.optional(),
    dashboardPrefs: dashboardPrefsSchema.optional(),
});
export const updateProjectSchema = z.object({
    title: z.string().min(1).max(255).optional(),
    currency: currencySchema.optional(),
    budgetTotal: z.number().positive().nullable().optional(),
    area: z.number().positive().nullable().optional(),
    startDate: startDateSchema.nullable().optional(),
    dashboardPrefs: dashboardPrefsSchema.optional(),
});
export const projectMemberRoleSchema = z.enum(['OWNER', 'EDITOR', 'VIEWER']);
export const projectSchema = z.object({
    id: z.string(),
    ownerId: z.string(),
    title: z.string(),
    type: projectTypeSchema.default('RENOVATION'),
    currency: currencySchema,
    budgetTotal: z.number().nullable(),
    area: z.number().nullable(),
    startDate: z.string().nullable(),
    /** Настройки главного экрана проекта; null — ещё не настраивали. */
    dashboardPrefs: dashboardPrefsSchema.nullable().default(null),
    createdAt: z.string(),
    updatedAt: z.string(),
    userRole: projectMemberRoleSchema.optional(),
    membersCount: z.number().optional(),
    memberLimit: z.number().default(PROJECT_MEMBER_LIMIT),
});
// Project Members
export const projectMemberUserSchema = z.object({
    id: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    username: z.string().nullable(),
});
export const projectMemberSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    userId: z.string(),
    role: projectMemberRoleSchema,
    joinedAt: z.string(),
    user: projectMemberUserSchema,
});
// Project Invitations
export const projectInvitationSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    token: z.string(),
    role: projectMemberRoleSchema,
    usesLeft: z.number().nullable(),
    expiresAt: z.string().nullable(),
    createdAt: z.string(),
    link: z.string(),
});
export const createInvitationSchema = z.object({
    role: z.enum(['EDITOR', 'VIEWER']).default('VIEWER'),
    usesLeft: z.number().positive().optional(),
    expiresInDays: z.number().positive().optional(),
});
export const invitationInfoSchema = z.object({
    projectTitle: z.string(),
    role: projectMemberRoleSchema,
    creatorName: z.string().nullable(),
    expiresAt: z.string().nullable(),
});
//# sourceMappingURL=project.js.map